import { memo, useCallback, useMemo } from 'react';

import { useFilters } from 'hooks/useFilters';

import {
  useGetMetricsQuery,
  useGetRegionsQuery,
  useGetTrendingGamesQuery,
} from 'store/api/configService/analyticsController';

import {
  appendTopGameColors,
  getPeriodValuesFromStr,
  mapOptions, // sortCountryOptions,
} from 'utils/analytics';

import FilterToggleGroup from 'components/UI/FilterToggleGroup/FilterToggleGroup';
import Loader from 'components/UI/Loader/Loader';
import NoData from 'components/UI/NoData/NoData';
import Typography from 'components/UI/Typography/Typography';
import Filters from 'components/pages/Analytics/Filters/Filters';
import GameBars from 'components/pages/Analytics/GameBars/GameBars';
import Section from 'components/pages/Analytics/Layout/Section/Section';
import SectionContent from 'components/pages/Analytics/Layout/SectionContent/SectionContent';
import SectionHeader from 'components/pages/Analytics/Layout/SectionHeader/SectionHeader';

import {
  DEFAULT_VALUES,
  LIMITS,
  OPTIONS,
  TOP_GAME_COLORS,
} from 'components/config/analytics';

import styles from './GamesByCategory.module.scss';

const defaultValues = DEFAULT_VALUES.byCategory;

//TODO: return back commented strings when filter by country logic will be needed
const GamesByCategory = () => {
  const {
    filters: { type, ...filters },
    setFilters,
    handleChange,
  } = useFilters({
    type: OPTIONS.gameTypes[0].value,
    period: OPTIONS.periodsByCategory[0].value,
    metric: defaultValues.metric,
    region: defaultValues.region,
    countries: [],
  });

  const { data, isFetching, isError } = useGetTrendingGamesQuery({
    categories: type,
    period: getPeriodValuesFromStr(filters.period),
    sortBy: filters.metric,
    limit: LIMITS.byCategory,
    ...(filters.region !== defaultValues.region && {
      regions: filters.region,
    }),
    ...(filters.countries.length && { countries: filters.countries.join(',') }),
  });

  const { data: metrics, isLoading: metricsLoading } = useGetMetricsQuery();
  const { data: regions, isLoading: regionsLoading } = useGetRegionsQuery();

  // const findRegionCountries = useCallback(
  //   (region) =>
  //     regions?.find(({ region: { code } }) => region === code)?.countries,
  //   [regions],
  // );

  const handleRegionChange = useCallback(
    (name, value) =>
      setFilters((prev) => ({
        ...prev,
        [name]: value,
        // countries: findRegionCountries(value)?.map(({ code }) => code) ?? [],
      })),
    [
      // findRegionCountries
    ],
  );

  // const countriesByRegion = useMemo(
  //   () => sortCountryOptions(mapOptions(findRegionCountries(filters.region))),
  //   [findRegionCountries, filters.region],
  // );

  const filtersOptions = useMemo(
    () => [
      {
        title: 'Choose period',
        label: 'period',
        value: filters.period,
        items: OPTIONS.periodsByCategory,
        handleSelect: handleChange,
      },
      {
        title: 'Choose metric',
        label: 'metric',
        value: filters.metric,
        items: metrics,
        handleSelect: handleChange,
        loading: metricsLoading,
      },
      {
        title: 'Region',
        label: 'region',
        value: filters.region,
        items: [
          {
            label: defaultValues.region,
            value: defaultValues.region,
          },
          ...mapOptions(regions?.map(({ region }) => region)),
        ],
        handleSelect: handleRegionChange,
        loading: regionsLoading,
      },
      // {
      //   title: 'Country',
      //   label: 'countries',
      //   value: filters.countries,
      //   items: countriesByRegion,
      //   handleSelect: handleChange,
      //   loading: regionsLoading,
      //   disabled: filters.region === defaultValues.region,
      //   countTags: 1,
      //   multiple: true,
      //   withSelectAll: true,
      //   fitContent: true,
      //   search: true,
      // },
    ],
    [
      filters,
      handleChange,
      metrics,
      metricsLoading,
      regions,
      regionsLoading,
      handleRegionChange,
      // countriesByRegion,
    ],
  );

  const games = useMemo(
    () => appendTopGameColors(data, TOP_GAME_COLORS.byCategory),
    [data],
  );

  return (
    <Section className={styles.section}>
      <SectionHeader
        headings={
          <>
            <Typography type="heading-md" weight="medium" uppercase>
              {LIMITS.byCategory} Trending games
            </Typography>
            <Typography type="text-lg" color="grey">
              Select a category, time period, metric, and region to view game
              performance
            </Typography>
          </>
        }
        filters={
          <>
            <FilterToggleGroup
              name="type"
              value={type}
              options={OPTIONS.gameTypes}
              onChange={handleChange}
            />
            <Filters options={filtersOptions} />
          </>
        }
      />
      {!isFetching ? (
        !isError && games?.length ? (
          <SectionContent type="bars">
            <GameBars games={games} />
          </SectionContent>
        ) : (
          <NoData />
        )
      ) : (
        <Loader />
      )}
    </Section>
  );
};

export default memo(GamesByCategory);
